/**
 * Lightweight first-party page-view tracking.
 *
 * Used to compute conversion funnels in the admin dashboard (e.g. how many
 * people landed on /the-menopause-invitation vs. how many bought it).
 *
 * Design notes:
 * - One persistent anonymous session ID per browser (stored in localStorage).
 *   The same person visiting twice on the same device = one unique visitor.
 * - No PII captured — just path + session ID + referrer hostname.
 * - Best-effort fire-and-forget. Tracking failures must never break the UI.
 * - Honours Do-Not-Track and bot user-agents.
 */
import { api } from "@/lib/api";

const STORAGE_KEY = "pb_session_id";
const sent = new Set(); // de-dupe identical (path, session) within a page life

function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid =
        "s_" +
        Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36).slice(-6);
      window.localStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    // localStorage may be unavailable (private mode, embedded contexts)
    return null;
  }
}

function shouldTrack() {
  if (typeof window === "undefined") return false;
  // Respect Do-Not-Track
  if (window.navigator?.doNotTrack === "1") return false;
  // Filter obvious bots (the real ones are filtered server-side too)
  const ua = (window.navigator?.userAgent || "").toLowerCase();
  if (
    /bot|crawler|spider|headlesschrome|puppeteer|playwright/.test(ua)
  ) {
    return false;
  }
  return true;
}

/**
 * Fire-and-forget page-view ping. Safe to call on every route mount.
 * No-ops if tracking is disabled, in SSR/prerender contexts, or if the
 * endpoint is unreachable.
 */
export function trackPageView(path) {
  if (!shouldTrack()) return;
  const sid = getSessionId();
  if (!sid) return;

  // De-dupe identical (path, session) within this page life
  const key = `${path}::${sid}`;
  if (sent.has(key)) return;
  sent.add(key);

  const referrer = (typeof document !== "undefined" && document.referrer) || "";
  // Only send the referrer when it's an external page (not our own SPA nav)
  let refToSend = "";
  try {
    if (referrer) {
      const refHost = new URL(referrer).hostname;
      if (!refHost.includes(window.location.hostname)) refToSend = referrer;
    }
  } catch {
    /* ignore */
  }

  // Fire-and-forget. We don't await and we swallow errors.
  api
    .post("/track", { path, session_id: sid, referrer: refToSend })
    .catch(() => {
      /* tracking must never break UX */
    });

  // Forward to Google Analytics 4 so SPA route changes are recorded as
  // distinct page_view events (gtag.js's auto pageview only fires once on
  // the initial document load).
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch {
    /* never break UX */
  }
}
